
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceDot, ScatterChart, Scatter } from 'recharts';
import { getEnergyBlock, ENERGY_THRESHOLDS } from '@/lib/energy';

interface EnhancedEnergyChartProps {
  energyForecast: number[];
  height?: number;
  showCurrentTime?: boolean;
  enableTooltip?: boolean;
  showLegend?: boolean;
  simplified?: boolean;
  wakeTimeHour?: number;
  startHour?: number; // new
  endHour?: number;   // new
}

// Energy zone colors matching the design
const ENERGY_BLOCK_COLORS = {
  power: '#dcfce7',  // light green
  focus: '#dbeafe',  // light blue  
  drift: '#f3e8ff',  // light purple
  rest: '#FFF',   // light gray
  ambient: '#dbeafe',
  ignore: '#f8fafc',
  any: '#f8fafc'
};

export const EnhancedEnergyChart: React.FC<EnhancedEnergyChartProps> = ({
  energyForecast,
  height = 200,
  showCurrentTime = false,
  enableTooltip = true,
  showLegend = false,
  simplified = false,
  wakeTimeHour = 7,
  startHour = 7, // default 7am
  endHour = 22   // default 10pm
}) => {
  // Convert energy forecast array to chart data format, only for startHour to endHour
  const chartData = React.useMemo(() => {
    if (!energyForecast || energyForecast.length === 0) {
      return [];
    }
    const data = energyForecast
      .map((energy, index) => {
        // Handle potential undefined/null values
        const safeEnergy = typeof energy === 'number' ? energy : 50;
        const clampedEnergy = Math.max(0, Math.min(100, safeEnergy));
        const displayHour = index;
        const timeLabel = displayHour === 0 ? '12am' : 
                         displayHour < 12 ? `${displayHour}am` : 
                         displayHour === 12 ? '12pm' : 
                         `${displayHour - 12}pm`;
        const energyBlock = getEnergyBlock(clampedEnergy);
        return {
          hour: index,
          displayHour: displayHour,
          energy: clampedEnergy,
          time: timeLabel,
          energyBlockType: energyBlock.type
        };
      })
      .filter(d => d.hour >= startHour && d.hour <= endHour);
    return data;
  }, [energyForecast, startHour, endHour]);

  // Current time dot data for scatter overlay
  const currentTimeScatterData = React.useMemo(() => {
    if (!showCurrentTime || chartData.length === 0) return [];
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeFloat = currentHour + currentMinute / 60;

    // Only show the dot if the current time is within the visible range
    if (currentTimeFloat < startHour || currentTimeFloat > endHour) return [];

    // Interpolate energy value between two nearest hours in the visible range
    const lowerHour = Math.max(Math.floor(currentTimeFloat), startHour);
    const upperHour = Math.min(Math.ceil(currentTimeFloat), endHour);
    const lower = chartData.find(d => d.hour === lowerHour);
    const upper = chartData.find(d => d.hour === upperHour);

    let energy = lower ? lower.energy : 0;
    if (lower && upper && lowerHour !== upperHour) {
      const t = (currentTimeFloat - lowerHour) / (upperHour - lowerHour);
      energy = lower.energy + t * (upper.energy - lower.energy);
    }

    return [{
      hour: currentTimeFloat,
      energy: energy
    }];
  }, [showCurrentTime, chartData, startHour, endHour]);

  console.log({energyForecast});

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const energy = payload[0].value;
      const dataPoint = payload[0]?.payload;
      const timeLabel = dataPoint?.time || `${dataPoint?.hour}:00`;
      const energyBlock = getEnergyBlock(energy);
      
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground">{timeLabel}</p>
          <p className="text-sm text-muted-foreground">
            Energy: <span className="font-semibold">
              {energy.toFixed(0)}%
            </span>
          </p>
          <p className="text-xs text-muted-foreground capitalize">{energyBlock.label}</p>
        </div>
      );
    }
    return null;
  };

  // Get energy level color based on value
  const getEnergyColor = (energy: number) => {
    if (energy >= ENERGY_THRESHOLDS.POWER) return ENERGY_BLOCK_COLORS.power; // High energy - green
    if (energy >= ENERGY_THRESHOLDS.FOCUS) return ENERGY_BLOCK_COLORS.focus; // Moderate energy - blue  
    if (energy >= ENERGY_THRESHOLDS.DRIFT) return ENERGY_BLOCK_COLORS.drift; // Moderate energy - blue  
    return ENERGY_BLOCK_COLORS.rest; // Low energy - red
  };
console.log({chartData})

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-muted-foreground text-sm">Loading energy data...</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
        
        {/* Power Energy threshold */}
        <ReferenceArea
          x1={startHour}
          x2={endHour}
          y1={ENERGY_THRESHOLDS.POWER}
          y2={100}
          fill={ENERGY_BLOCK_COLORS.power}
          fillOpacity={0.6}
        />

        {/* Focus Energy threshold */}
        <ReferenceArea
          x1={startHour}
          x2={endHour}
          y1={ENERGY_THRESHOLDS.FOCUS}
          y2={ENERGY_THRESHOLDS.POWER}
          fill={ENERGY_BLOCK_COLORS.focus}
          fillOpacity={0.6}
        />

        {/* Drift Energy threshold */}
        <ReferenceArea
          x1={startHour}
          x2={endHour}
          y1={ENERGY_THRESHOLDS.DRIFT}
          y2={ENERGY_THRESHOLDS.FOCUS}
          fill={ENERGY_BLOCK_COLORS.drift}
          fillOpacity={0.6}
        />

        {/* Rest Energy threshold */}
        <ReferenceArea
          x1={startHour}
          x2={endHour}
          y1={0}
          y2={ENERGY_THRESHOLDS.DRIFT}
          fill={ENERGY_BLOCK_COLORS.rest}
          fillOpacity={0.6}
        />
        
        {/* {!simplified && (
          <XAxis 
            dataKey="hour"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            interval={2}
            domain={[startHour, endHour]}
            type="number"
            tickFormatter={(value) => {
              const hour = parseInt(value);
              return hour === 0 ? '12am' : 
                     hour < 12 ? `${hour}am` : 
                     hour === 12 ? '12pm' : 
                     `${hour - 12}pm`;
            }}
          />
        )} */}
        
        {!simplified  ? (
          <YAxis 
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            tickFormatter={(value) => `${value}%`}
          />
        ): 
        <YAxis 
            domain={[ENERGY_THRESHOLDS.DRIFT, 100]}
            hide={true}
          />
        }
        
        {/* Add XAxis for proper coordinate system even when hidden */}
        <XAxis 
          dataKey="hour"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          interval={0}
          domain={[startHour, endHour]}
          type="number"
          hide={simplified}
          ticks={Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i)}
          tickFormatter={(hour) => {
            if (hour === 0) return '12am';
            if (hour < 12) return `${hour}am`;
            if (hour === 12) return '12pm';
            return `${hour - 12}pm`;
          }}
        />

        {enableTooltip && <Tooltip content={<CustomTooltip />} />}
        
        <Line
          type="monotone"
          dataKey="energy"
          stroke="hsl(var(--primary))"
          strokeWidth={simplified ? 1.5 : 2}
          dot={false}
          activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
          animationDuration={0}
        />
        {/* Current time dot using ReferenceDot with proper coordinates */}
        {currentTimeScatterData.length > 0 && (
          <ReferenceDot
            x={currentTimeScatterData[0].hour}
            y={currentTimeScatterData[0].energy}
            r={6}
            fill="#111"
            stroke="#fff"
            strokeWidth={2}
            isFront={true}
            ifOverflow="visible"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
